import { useCallback } from "react";

export const useHandleMetadataUpdate = (
  selectedRows,
  originalRowData,
  setRowData,
  setChangedRows,
  gridRef
) => {
  return useCallback(
    (updates) => {
      if (!selectedRows || selectedRows.length === 0) return;

      const selectedRowIds = selectedRows.map((row) => row._id);

      setRowData((prev) => {
        const updatedRows = [...prev];

        selectedRowIds.forEach((id) => {
          const index = updatedRows.findIndex((row) => row._id === id);
          if (index !== -1) {
            const rowToUpdate = updatedRows[index];
            const updatedRow = { ...rowToUpdate };
            const originalRow = originalRowData.find((r) => r._id === id) || {};

            // Generic update handler for any field
            const updateField = (
              fieldName,
              newValue,
              originalFieldName = fieldName
            ) => {
              updatedRow[fieldName] = newValue;
              const origValue = originalRow[originalFieldName];

              if (newValue !== origValue) {
                setChangedRows((prevArr) => {
                  const exists = prevArr.find((item) => item._id === id);
                  if (exists) {
                    return prevArr.map((item) =>
                      item._id === id
                        ? { ...item, [fieldName]: newValue }
                        : item
                    );
                  } else {
                    return [...prevArr, { _id: id, [fieldName]: newValue }];
                  }
                });
              } else {
                // Remove from changed rows if reverted to original
                setChangedRows((prevArr) => {
                  const exists = prevArr.find((item) => item._id === id);
                  if (!exists) return prevArr;
                  const updated = { ...exists };
                  delete updated[fieldName];
                  const keys = Object.keys(updated);
                  if (keys.length === 1 && keys[0] === "_id") {
                    return prevArr.filter((item) => item._id !== id);
                  }
                  return prevArr.map((item) =>
                    item._id === id ? updated : item
                  );
                });
              }
            };

            // Handle each update type
            Object.entries(updates).forEach(([key, value]) => {
              switch (key) {
                case "newDocGroup":
                  updateField("docGroup", value);
                  // Clear docType when category changes
                  updatedRow.docType = "";
                  updateField("docType", "", "docType");
                  break;

                case "newDocType":
                  if (updates.newDocGroup === undefined) {
                    updateField("docType", value || "");
                  }
                  break;

                case "newEffectiveDate":
                  let newISO = "";
                  if (value instanceof Date) {
                    newISO = value.toISOString();
                  } else {
                    newISO = value;
                  }
                  updateField("reportEffectiveDate", newISO);
                  break;

                case "newGuidelineExpirationDays":
                  updateField("reportGuidelineExpirationDays", value);
                  break;

                case "newDocumentStatus":
                case "documentStatus":
                  updateField("documentStatus", value);
                  break;

                case "isHidden":
                  updateField("isHidden", value);
                  break;

                case "reportEffectiveDate":
                  let dateISO = "";
                  if (value instanceof Date) {
                    dateISO = value.toISOString();
                  } else {
                    dateISO = value;
                  }
                  updateField("reportEffectiveDate", dateISO);
                  break;

                case "reportGuidelineExpirationDays":
                  updateField("reportGuidelineExpirationDays", value);
                  break;

                // Generic handler for any other fields
                default:
                  updateField(key, value);
                  break;
              }
            });

            updatedRows[index] = updatedRow;
          }
        });

        return updatedRows;
      });

      // Force refresh the grid
      setTimeout(() => {
        if (gridRef.current && gridRef.current.api) {
          gridRef.current.api.refreshCells();
        }
      }, 0);
    },
    [selectedRows, originalRowData, setRowData, setChangedRows, gridRef]
  );
};
